import { z } from "zod";

/**
 * Concrete per-arm settings overrides a voice experiment applies. Single source of
 * truth for the override shape across the stack: the transcript tag + ingest payload validate
 * against it, the experiment definitions/evaluator type against the inferred type, and the agent's
 * apply seams reuse the inferred override types.
 *
 * `reasoningEffort` uses `z.enum` so its inferred type is the string-literal union — kept assignable
 * to the `@livekit/agents` inference `reasoning_effort` type without casts. See
 * livekit/docs/voice-agent-experiments.md.
 */
export const voiceLlmOverrideSchema = z.object({
  model: z.string().optional(),
  provider: z.string().optional(),
  reasoningEffort: z.enum(["none", "minimal", "low", "medium", "high"]).optional(),
});

export type VoiceLlmOverride = z.infer<typeof voiceLlmOverrideSchema>;

export const voiceSpeechModelOverrideSchema = z.object({
  model: z.string().trim().min(1),
});

export type VoiceSpeechModelOverride = z.infer<typeof voiceSpeechModelOverrideSchema>;

export const voiceEndpointingProfileSchema = z
  .object({
    mode: z.literal("dynamic"),
    minDelay: z.number().int().min(100).max(1_000),
    maxDelay: z.number().int().min(500).max(5_000),
    alpha: z.number().min(0).max(1),
  })
  .refine(({ minDelay, maxDelay }) => minDelay <= maxDelay, {
    message: "Endpointing minDelay must not exceed maxDelay",
  });

export type VoiceEndpointingProfile = z.infer<typeof voiceEndpointingProfileSchema>;

export const slowThinkerEndpointingOverrideSchema = z.object({
  defaultProfile: voiceEndpointingProfileSchema,
  deliberateProfile: voiceEndpointingProfileSchema.optional(),
});

export type SlowThinkerEndpointingOverride = z.infer<typeof slowThinkerEndpointingOverrideSchema>;

export enum VoiceAgentRuntime {
  Legacy = "legacy",
  SlowThinker = "slow_thinker",
}

export const slowThinkerExperimentOverridesSchema = z.object({
  endpointing: slowThinkerEndpointingOverrideSchema.optional(),
  preemptiveThinker: z.boolean().optional(),
  stagedRevision: z.boolean().optional(),
  thinker: voiceLlmOverrideSchema.optional(),
});

export const voiceExperimentOverridesSchema = z.object({
  agentRuntime: z.nativeEnum(VoiceAgentRuntime).optional(),
  llm: voiceLlmOverrideSchema.optional(),
  slowThinker: slowThinkerExperimentOverridesSchema.optional(),
  stt: voiceSpeechModelOverrideSchema.optional(),
  tts: voiceSpeechModelOverrideSchema.optional(),
});

export type VoiceExperimentOverrides = z.infer<typeof voiceExperimentOverridesSchema>;
